// caiet UC61-02/03 — the Specific Observer types are open, so no screen carries a
// list of them. The role codes SIMSM currently defines, and the one way to turn
// a code into something a person can read: the translated name when the
// institution is one the product ships copy for, otherwise the display name the
// System Administrator gave the role, and only then the raw code.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { scopeHoldsImsp } from '../app/access-model';
import { apiClient } from '../app/api-client';

export interface ObserverRoleName {
  roleCode: string;
  displayName: string;
  description: string;
  // Whether the role may still be granted. A deactivated role keeps every
  // dashboard it was already shared with (`analytics.dashboard_config.role_scope`
  // has no foreign key and revoking on deactivation is not what UC61-02 asks
  // for), so it must still be nameable long after it stops being offerable.
  active: boolean;
}

// The roles the product ships translated copy for. Anything else is named by
// the display name the System Administrator gave it — that is the whole point
// of UC61-03, so this map must never be used as a list of valid roles.
export const ROLE_LABEL_KEYS: Record<string, string> = {
  SYSTEM_ADMIN: 'role.system_admin',
  REGISTRAR_ADMIN: 'role.registrar_admin',
  REGISTRAR_OPERATOR: 'role.registrar_operator',
  OBSERVER: 'role.observer',
  OBSERVER_MS: 'role.observer_ms',
  OBSERVER_CNAM: 'role.observer_cnam',
  OBSERVER_ANSP: 'role.observer_ansp',
  OBSERVER_VAMAL: 'role.observer_vamal',
  OBSERVER_CAPCS: 'role.observer_capcs',
  // Not a person: the machine caller of the UC15/UC42 System API, which the
  // caiet's actor table calls "Sistem Registrator". It reaches this map through
  // the catalog history's `actor_role`, never through a session.
  REGISTRAR_SYSTEM: 'role.registrar_system',
};

// caiet UC10-02 / NC-ACT-04: an Operator or Administrator of a Registrar de tip
// IMSP is a role of its own, and the role code does not say so — the type of the
// Registrar in the role's scope does. Only these two roles have the variant.
// per docs/shape-ops-role-label-imsp.md §2
const IMSP_ROLE_LABEL_KEYS: Record<string, string> = {
  REGISTRAR_ADMIN: 'role.registrar_admin_imsp',
  REGISTRAR_OPERATOR: 'role.registrar_operator_imsp',
};

// The translation key a role is named by, or undefined for a code the product
// ships no copy for (an Observer type minted in MPass, UC61-02/03), which the
// callers name from the directory. `registrarKinds` is the role's own scope, and
// a scope holding any IMSP Registrar names the IMSP variant, whichever Registrar
// is active: the label is the role's, the "Stocuri IMSP" entry the active
// Registrar's (docs/shape-ops-tenant-selector.md §7.2).
export function roleLabelKey(
  roleCode: string,
  registrarKinds?: ReadonlyArray<string>,
): string | undefined {
  const imspKey = IMSP_ROLE_LABEL_KEYS[roleCode];
  if (imspKey && scopeHoldsImsp(registrarKinds)) return imspKey;
  return ROLE_LABEL_KEYS[roleCode];
}

// Every role SIMSM defines, deactivated ones included. This is the naming
// source, and naming is not offering: a screen that OFFERS a role to grant asks
// `useObserverRoles` below instead.
export function useObserverRoleDirectory(): UseQueryResult<ObserverRoleName[]> {
  return useQuery({
    queryKey: ['observer-roles'],
    queryFn: () =>
      apiClient.get<{ roles: ObserverRoleName[] }>('/observer-roles').then((r) => r.data.roles),
    staleTime: 5 * 60_000,
  });
}

// The roles that may still be granted — the checkbox lists, the role filters and
// the role-first tab. Deliberately the same request as the directory (one fetch,
// one cache entry) narrowed on the way out, so a deactivated role never appears
// as something an administrator can share a dashboard with.
export function useObserverRoles(): UseQueryResult<ObserverRoleName[]> {
  return useQuery({
    queryKey: ['observer-roles'],
    queryFn: () =>
      apiClient.get<{ roles: ObserverRoleName[] }>('/observer-roles').then((r) => r.data.roles),
    select: (roles) => roles.filter((role) => role.active),
    staleTime: 5 * 60_000,
  });
}

// The label of a role as one of the person's own roles: the IMSP variant when
// the role's scope holds an IMSP Registrar. The Registrar types are required, so
// the resolver cannot be handed to `Array.map` by mistake, which would pass the
// index as the types; a screen naming a role code with no scope in hand (a log,
// a share list) asks `useRoleLabel` below.
export function useScopedRoleLabel(): (
  roleCode: string,
  registrarKinds: ReadonlyArray<string> | undefined,
) => string {
  const { t } = useTranslate();
  // The directory, not the offer list: a dashboard shared with a role that has
  // since been deactivated printed the raw code — `OBSERVER_VERIF` — because the
  // only list the resolver could read had already dropped it.
  const roles = useObserverRoleDirectory();
  return (roleCode: string, registrarKinds: ReadonlyArray<string> | undefined): string => {
    const key = roleLabelKey(roleCode, registrarKinds);
    if (key) {
      return t(key);
    }
    return roles.data?.find((role) => role.roleCode === roleCode)?.displayName ?? roleCode;
  };
}

// The family name of a role code, with no Registrar in hand.
export function useRoleLabel(): (roleCode: string) => string {
  const scoped = useScopedRoleLabel();
  return (roleCode: string): string => scoped(roleCode, undefined);
}
